"use client"

import { useCart } from "@/lib/context/cart-context"
import { dispatchCartDrawerClosed } from "@/lib/data/cart-event-bus"
import { getCheckoutStep } from "@/lib/util/get-checkout-step"
import { convertToLocale } from "@/lib/util/money"
import { CartDrawerContent } from "@/modules/cart/components/cart-drawer-content"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import { B2BCustomer } from "@/types"
import { StoreFreeShippingPrice } from "@/types/shipping-option/http"
import { clx, Drawer } from "@medusajs/ui"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"

type CartDrawerProps = {
  customer: B2BCustomer | null
  freeShippingPrices: StoreFreeShippingPrice[]
}

const CartDrawer = ({
  customer,
  freeShippingPrices,
  ...props
}: CartDrawerProps) => {
  const [activeTimer, setActiveTimer] = useState<NodeJS.Timer | undefined>(
    undefined
  )
  const [isOpen, setIsOpen] = useState(false)

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      dispatchCartDrawerClosed()
    }
  }

  const { cart } = useCart()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isExpressCheckout =
    searchParams?.get("express") === "true" ||
    pathname.includes("/express-checkout")

  const items = cart?.items || []

  const totalItems =
    items?.reduce((acc, item) => {
      return acc + item.quantity
    }, 0) || 0

  /**
   * Amount shown next to cart icon: items + tax only (no shipping).
   * So the badge is "what you pay for the products" and never lower than that; shipping is shown in cart/checkout only.
   */
  const displayAmount = useMemo(() => {
    if (!cart) return 0

    const shippingTotal = cart.shipping_total ?? 0

    // Prefer: cart total minus shipping (items + tax only)
    if (cart.total != null && cart.total > 0) {
      return Math.max(0, cart.total - shippingTotal)
    }

    // Fallback: subtotal + tax (items with tax, no shipping)
    const itemsWithTax = (cart.subtotal ?? 0) + (cart.tax_total ?? 0)
    if (itemsWithTax > 0) {
      return itemsWithTax
    }

    // item_total is items incl. tax
    if (cart.item_total != null && cart.item_total > 0) {
      return cart.item_total
    }

    const itemsTotal = items.reduce((sum, item) => sum + (item.total ?? 0), 0)
    if (itemsTotal > 0) {
      return itemsTotal
    }

    return cart.item_subtotal ?? 0
  }, [cart, items])

  const itemRef = useRef<number>(totalItems || 0)

  const timedOpen = () => {
    if (isOpen) {
      return
    }

    open()

    const timer = setTimeout(close, 5000)

    setActiveTimer(timer)
  }

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => {
      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [activeTimer])

  const cancelTimer = () => {
    if (activeTimer) {
      clearTimeout(activeTimer)
    }
  }

  // open cart dropdown when modifying the cart items, but only if we're not on the cart page
  useEffect(() => {
    const shouldAutoOpen =
      itemRef.current !== totalItems &&
      !pathname.includes("/cart") &&
      !pathname.includes("/account") &&
      !isExpressCheckout
    if (shouldAutoOpen) {
      timedOpen()
    }
    itemRef.current = totalItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItems, isExpressCheckout, pathname])

  //close cart drawer when navigating to a different page
  useEffect(() => {
    cancelTimer()
    close()
  }, [pathname])

  const isCartPage = pathname.includes("/cart")

  return (
    <>
      {isOpen && (
        <div className="fixed inset-[-2rem] z-10 backdrop-blur-sm p-0" />
      )}
      <Drawer
        onMouseEnter={cancelTimer}
        className="rounded-none m-0 p-0 bg-none z-50"
        open={isOpen}
        onOpenChange={handleOpenChange}
        {...(props as any)}
      >
        <Drawer.Trigger asChild>
          <button type="button" className="hover:text-ui-fg-base">
            {/* Desktop and regular button */}
            <span className="transition-fg relative hidden small:inline-flex w-fit items-center justify-center overflow-hidden outline-none txt-compact-small-plus gap-x-1.5 px-3 py-1.5 rounded-full hover:bg-neutral-100">
              <ShoppingBag />
              <span className="text-sm font-normal hidden small:inline-block">
                {cart && items && items.length > 0
                  ? convertToLocale({
                      amount: displayAmount,
                      currency_code: cart.currency_code,
                    })
                  : "Košarica"}
              </span>
              <span className="bg-blue-500 text-white text-xs px-1.5 py-px rounded-full">
                {totalItems}
              </span>
            </span>
            {/* Mobile Nav Button */}
            <span className="flex flex-col items-center justify-center px-3 py-2 min-w-0 flex-1 relative group small:hidden">
              {isCartPage && (
                <span className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-blue-500 rounded-full"></span>
              )}
              <span className="relative">
                <ShoppingBag
                  size="22"
                  className={clx(
                    "mb-1 transition-colors",
                    isCartPage
                      ? "text-blue-500"
                      : "text-neutral-500 group-hover:text-neutral-900"
                  )}
                />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-2 bg-blue-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </span>
              <span
                className={clx(
                  "text-xs font-medium transition-colors",
                  isCartPage
                    ? "text-blue-500"
                    : "text-neutral-500 group-hover:text-neutral-900"
                )}
              >
                Košarica
              </span>
            </span>
          </button>
        </Drawer.Trigger>
        <Drawer.Content
          className="z-[70] rounded-none m-0 p-0 inset-y-0 sm:right-0"
          overlayProps={{ className: "z-[70]" }}
          onMouseEnter={cancelTimer}
        >
          <Drawer.Title className="sr-only">Košarica</Drawer.Title>
          <Drawer.Description className="sr-only">
            Pregled izdelkov v košarici
          </Drawer.Description>
          <CartDrawerContent
            customer={customer}
            freeShippingPrices={freeShippingPrices}
            onClose={close}
          />
        </Drawer.Content>
      </Drawer>
    </>
  )
}

export default CartDrawer
